import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtUSD } from "@/lib/format";
import {
  Calculator, Target, Scale, TrendingUp, ArrowRight, TrendingDown, CheckCircle2,
} from "lucide-react";

const actions = [
  { title: "Calculate My Shift Pay", desc: "Know what a single shift really earns.", btn: "Calculate Shift", to: "/shift", icon: Calculator },
  { title: "Reach an Income Goal", desc: "See the extra shifts to hit your target.", btn: "Build My Plan", to: "/income-goal", icon: Target },
  { title: "Compare Two Jobs", desc: "Weigh two offers side-by-side.", btn: "Compare Offers", to: "/compare", icon: Scale },
  { title: "Explore Career Paths", desc: "Discover your next nursing move.", btn: "Explore", to: "/career", icon: TrendingUp, soon: true },
];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-[#18A6A6]">Dashboard</p>
        <h1 className="mt-1 font-heading text-3xl font-extrabold text-[#0F2747]" data-testid="dashboard-greeting">
          Welcome back, {user?.first_name || "Nurse"}
        </h1>
      </div>

      {/* Stat cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm" data-testid="stat-current-income">
          <p className="text-xs font-bold uppercase tracking-widest text-[#18A6A6]">Current Estimated Income</p>
          <p className="mt-2 font-heading text-3xl font-extrabold text-[#0F2747]">{fmtUSD(d?.current_annual_income)}</p>
          <p className="mt-1 text-sm text-slate-500">{fmtUSD(d?.monthly_income)} / month (gross)</p>
        </div>
        <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm" data-testid="stat-income-goal">
          <p className="text-xs font-bold uppercase tracking-widest text-[#18A6A6]">Income Goal</p>
          <p className="mt-2 font-heading text-3xl font-extrabold text-[#0F2747]">{fmtUSD(d?.income_goal)}</p>
          <p className="mt-1 text-sm text-slate-500">Your annual target</p>
        </div>
        <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm" data-testid="stat-income-gap">
          <p className="text-xs font-bold uppercase tracking-widest text-[#D6A84B]">Income Gap</p>
          {d?.goal_reached ? (
            <p className="mt-2 flex items-center gap-2 font-heading text-xl font-bold text-[#10B981]">
              <CheckCircle2 className="h-5 w-5" /> Goal reached
            </p>
          ) : (
            <p className="mt-2 font-heading text-3xl font-extrabold text-[#D6A84B]">{fmtUSD(d?.income_gap)}</p>
          )}
          <p className="mt-1 text-sm text-slate-500">{d?.goal_reached ? "You're at or above your goal." : "Away from your goal"}</p>
        </div>
      </div>

      {/* Progress */}
      {d?.income_goal > 0 && (
        <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between text-sm font-medium text-slate-600">
            <span className="flex items-center gap-2">
              {d.goal_reached ? <TrendingUp className="h-4 w-4 text-[#10B981]" /> : <TrendingDown className="h-4 w-4 text-[#D6A84B]" />}
              {d.progress}% of your {fmtUSD(d.income_goal)} goal
            </span>
            <span className="font-heading font-bold text-[#0F2747]">{d.progress}%</span>
          </div>
          <div className="mt-3 h-3 rounded-full bg-slate-100 p-0.5">
            <div className="h-full rounded-full bg-gradient-to-r from-[#18A6A6] to-[#20C997] transition-all duration-500"
              style={{ width: `${Math.max(4, d.progress)}%` }} />
          </div>
          {!d.goal_reached && d.income_gap > 0 && (
            <p className="mt-3 text-sm text-slate-500">
              You're <b className="text-[#0F2747]">{fmtUSD(d.income_gap)}</b> away from your annual income goal.
            </p>
          )}
        </div>
      )}

      {/* Action cards */}
      <div className="grid gap-4 sm:grid-cols-2">
        {actions.map(({ title, desc, btn, to, icon: Icon, soon }) => (
          <div key={title} className="group flex flex-col rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm transition-all hover:shadow-md">
            <div className="flex items-start justify-between">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#EAF4FA] text-[#18A6A6]">
                <Icon className="h-6 w-6" />
              </div>
              {soon && <span className="rounded-full bg-[#D6A84B]/20 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-[#B8862F]">Coming Soon</span>}
            </div>
            <h3 className="mt-4 font-heading text-lg font-semibold text-[#0F2747]">{title}</h3>
            <p className="mt-1 flex-1 text-sm text-slate-600">{desc}</p>
            <button data-testid={`action-${to.slice(1)}`} onClick={() => navigate(to)}
              className="mt-4 inline-flex items-center gap-2 self-start rounded-full bg-[#0F2747] px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-[#16355F]">
              {btn} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      <p className="text-xs text-slate-400">
        All figures are estimated gross income before taxes and deductions. Not financial, tax, or employment advice.
      </p>
    </div>
  );
}
